import { createAccessCodePlugin, type AccessState } from './access-code-plugin';
import {
  clearDraft,
  extractSurveyState,
  getMissingAnswers,
  loadDraft,
  restoreSurveyState,
  saveDraft,
  setSurveyAccessCode,
  setSurveyQuestionCount,
} from './document-state';
import { api } from './http';
import type { HvyModule, HvyMount } from './hvy-types';
import { pageLayout } from './layout';
import { surveyDisplayName } from './survey-display';
import {
  isAccessCode,
  normalizeAccessCode,
  type SubmissionPayload,
  type QuestionId,
  type SurveyCatalogEntry,
} from '../shared/survey';

export async function renderSurveyPage(root: HTMLElement, options: { example?: boolean } = {}): Promise<void> {
  const example = options.example === true;
  root.innerHTML = pageLayout('Land Use Survey', `
    ${example ? '<section class="card example-survey-toolbar"><label>Survey<select id="exampleSurveyPicker"></select></label></section>' : ''}
    <section class="survey-card${example ? ' example-survey' : ''}">
      <div id="surveyLoading" class="loading-state"><span class="spinner"></span>Loading the survey…</div>
      <div id="surveyMount" class="survey-mount survey-locked"></div>
      <div id="surveyActions" class="survey-actions" hidden>
        ${example
          ? '<p class="survey-example-notice">Example only — responses cannot be submitted.</p>'
          : '<p id="surveyValidation" class="form-message" role="alert"></p><button id="submitSurvey" class="primary-button submit-button" type="button">Submit completed survey</button>'}
      </div>
    </section>`, example
      ? { publicNav: true, showAbout: false, showHeading: false }
      : { showHeader: false, showHeading: false });

  const mountRoot = root.querySelector<HTMLElement>('#surveyMount')!;
  const loading = root.querySelector<HTMLElement>('#surveyLoading')!;
  const actions = root.querySelector<HTMLElement>('#surveyActions')!;
  const validation = root.querySelector<HTMLElement>('#surveyValidation');
  const submitButton = root.querySelector<HTMLButtonElement>('#submitSurvey');
  const exampleSurveyPicker = root.querySelector<HTMLSelectElement>('#exampleSurveyPicker');
  let accessState: AccessState = { status: 'idle', code: '' };
  let activeSurvey: SurveyCatalogEntry;
  let surveyCatalog: SurveyCatalogEntry[] = [];
  let hvy: HvyModule;
  let mount: HvyMount | null = null;
  let saveTimer = 0;
  let loadSequence = 0;

  const saveBeforeUnload = () => {
    if (!example && mount) saveDraft(mount.getDocument(), localStorage, activeSurvey.id, activeSurvey.version, activeSurvey.key);
  };
  if (!example) window.addEventListener('beforeunload', saveBeforeUnload);

  const mountSurvey = async (survey: SurveyCatalogEntry, accessCode: string): Promise<void> => {
    const sequence = ++loadSequence;
    loading.hidden = false;
    loading.style.display = '';
    loading.className = 'loading-state';
    loading.innerHTML = '<span class="spinner"></span>Loading the survey…';
    actions.hidden = true;
    mountRoot.classList.add('survey-locked');
    const response = await fetch(survey.documentPath);
    if (!response.ok) throw new Error('The survey file could not be loaded.');
    const document = hvy.deserializeDocumentBytes(new Uint8Array(await response.arrayBuffer()), '.hvy');
    setSurveyQuestionCount(document, survey.questionCount);
    if (sequence !== loadSequence) return;

    const draft = example ? null : loadDraft(localStorage, survey.id, survey.version, survey.key);
    if (draft && (!accessCode || draft.accessCode === accessCode)) restoreSurveyState(document, draft);
    if (accessCode) setSurveyAccessCode(document, accessCode);

    window.clearTimeout(saveTimer);
    mount?.destroy();
    mountRoot.replaceChildren();
    activeSurvey = survey;
    if (exampleSurveyPicker) exampleSurveyPicker.value = survey.key;
    mount = hvy.mountHvyViewer({
      root: mountRoot,
      document,
      plugins: [createAccessCodePlugin((next) => onAccessStateChanged(next, survey)), hvy.plugins.editableText, hvy.plugins.scripting],
      controls: false,
      persistSessionState: false,
      onDocumentChange() {
        if (example || !mount || activeSurvey.id !== survey.id) return;
        window.clearTimeout(saveTimer);
        saveTimer = window.setTimeout(() => saveDraft(mount!.getDocument(), localStorage, survey.id, survey.version, survey.key), 120);
      },
    });
    if (example) {
      mountRoot.classList.remove('survey-locked');
      actions.hidden = false;
    }
    loading.hidden = true;
    loading.style.display = 'none';
  };

  const onAccessStateChanged = (next: AccessState, mountedSurvey: SurveyCatalogEntry) => {
    if (mountedSurvey.id !== activeSurvey.id) return;
    if (example) {
      mountRoot.classList.remove('survey-locked');
      actions.hidden = false;
      return;
    }
    accessState = next;
    validation!.textContent = '';
    if (next.status === 'valid' && next.surveyId !== mountedSurvey.id) {
      const survey = surveyCatalog.find((entry) => entry.key === next.surveyKey);
      if (survey) void mountSurvey(survey, next.code).catch(showLoadError);
      return;
    }
    const valid = next.status === 'valid';
    mountRoot.classList.toggle('survey-locked', !valid);
    actions.hidden = !valid;
    if (mount) {
      window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(() => saveDraft(mount!.getDocument(), localStorage, activeSurvey.id, activeSurvey.version, activeSurvey.key), 120);
    }
  };

  const showLoadError = (error: unknown) => {
    loading.hidden = false;
    loading.style.display = '';
    loading.className = 'error-state';
    loading.textContent = error instanceof Error ? error.message : 'The survey could not be loaded.';
  };

  try {
    const hvyModuleUrl = __HVY_EMBED_URL__;
    const [hvyModule, catalogResponse] = await Promise.all([
      import(/* @vite-ignore */ hvyModuleUrl) as Promise<HvyModule>,
      api<{ surveys: SurveyCatalogEntry[] }>('/api/surveys'),
    ]);
    hvy = hvyModule;
    surveyCatalog = catalogResponse.surveys;
    if (exampleSurveyPicker) {
      exampleSurveyPicker.innerHTML = surveyCatalog.map((survey) => `<option value="${survey.key}">${surveyDisplayName(survey.key, survey.label)}</option>`).join('');
    }
    activeSurvey = surveyCatalog.find((survey) => survey.key === 'original')!;
    if (!activeSurvey) throw new Error('The original survey is not configured.');
    const queryCode = normalizeAccessCode(new URLSearchParams(window.location.search).get('accessCode') ?? '');
    await mountSurvey(activeSurvey, isAccessCode(queryCode) ? queryCode : '');
    exampleSurveyPicker?.addEventListener('change', () => {
      const survey = surveyCatalog.find((entry) => entry.key === exampleSurveyPicker.value);
      if (survey) void mountSurvey(survey, '').catch(showLoadError);
    });
  } catch (error) {
    showLoadError(error);
    return;
  }

  submitButton?.addEventListener('click', async () => {
    if (!mount || accessState.status !== 'valid' || accessState.surveyId !== activeSurvey.id) return;
    const missing = getMissingAnswers(mount.getDocument(), activeSurvey.questionIds);
    if (missing.length > 0) {
      const questionIds: readonly QuestionId[] = activeSurvey.questionIds;
      validation!.textContent = `Please answer ${missing.map((id) => `Question ${questionIds.indexOf(id) + 1}`).join(', ')}.`;
      return;
    }
    const state = extractSurveyState(mount.getDocument());
    const payload: SubmissionPayload = {
      submissionId: crypto.randomUUID(),
      surveyId: activeSurvey.id,
      surveyVersion: activeSurvey.version,
      accessCode: accessState.code,
      answers: state.answers,
      feedback: state.feedback,
    };
    submitButton.disabled = true;
    submitButton.textContent = 'Submitting…';
    validation!.textContent = '';
    try {
      await api('/api/submissions', { method: 'POST', body: JSON.stringify(payload) });
      clearDraft(localStorage, activeSurvey.id, activeSurvey.key);
      window.removeEventListener('beforeunload', saveBeforeUnload);
      mount.destroy();
      root.innerHTML = pageLayout('Response received', `
        <section class="card confirmation-card">
          <span class="success-mark" aria-hidden="true">✓</span>
          <h2>Thank you for completing the survey.</h2>
          <p>Your response was recorded.</p>
          <p>You may close this page. To submit another household response, open the survey again and re-enter the access code.</p>
        </section>`, { showHeader: false, eyebrow: 'Petrovitsky Park Open Space & Trail Community Survey' });
    } catch (error) {
      validation!.textContent = error instanceof Error ? error.message : 'The response could not be submitted.';
      submitButton.disabled = false;
      submitButton.textContent = 'Submit completed survey';
    }
  });
}
